import { useEffect, useState } from "react";
import {
  useEinheiten,
  useFirmenstamm,
  useKatalog,
  useNummernkreise,
  useSaveEinheiten,
  useSaveFirmenstamm,
  useSaveKatalog,
  useSaveNummernkreis,
  useSaveTextbausteine,
  useTextbausteine,
  useVorlagen,
  useDeleteVorlage,
} from "../../hooks/useStammdaten";
import type {
  Firmenstamm,
  KatalogPosition,
  Nummernkreis,
  TextBaustein,
  Textbausteine,
} from "../../lib/types";
import { errorText } from "../../lib/invoices";
import "../../styles/stammdaten.css";

/**
 * Belegstammdaten pflegen.
 *
 * Firmenstamm, Textbausteine, Positionskatalog und Einheiten liegen als
 * Dateien neben der gemeinsamen PDF-Engine — dieselben, die die Desktop-App
 * liest. Wer hier speichert, ändert beide Apps. Der Nummernkreis liegt in
 * Postgres und ist für beide verbindlich.
 *
 * Jeder Bereich ist eine eigene Karte, die zugeklappt nur den Stand zeigt.
 * Vier gleichzeitig ausgeklappte Formulare wären eine unbedienbare Wand.
 */

function Karte({ titel, kicker, stand, kind, children }: {
  titel: string;
  kicker: string;
  stand: string;
  kind?: React.ReactNode;
  children: (schliessen: () => void) => React.ReactNode;
}) {
  const [offen, setOffen] = useState(false);
  return (
    <section className="sd-karte">
      <header>
        <div>
          <p className="sd-kicker">{kicker}</p>
          <h2>{titel}</h2>
          <span className="sd-stand">{stand}</span>
        </div>
        <div className="sd-kopf-aktionen">
          {kind}
          <button type="button" className="sd-btn" onClick={() => setOffen((v) => !v)}>
            {offen ? "Schließen" : "Bearbeiten"}
          </button>
        </div>
      </header>
      {offen && <div className="sd-inhalt">{children(() => setOffen(false))}</div>}
    </section>
  );
}

function Meldung({ ok, fehler }: { ok?: string; fehler?: unknown }) {
  if (fehler) return <p className="sd-fehler" role="alert">{errorText(fehler)}</p>;
  if (ok) return <p className="sd-ok" role="status">{ok}</p>;
  return null;
}

/* ══ Firmenstamm ═════════════════════════════════════════════════════════ */

function FirmenstammKarte() {
  const abfrage = useFirmenstamm();
  const speichern = useSaveFirmenstamm();
  const [entwurf, setEntwurf] = useState<Firmenstamm | null>(null);
  const [gespeichert, setGespeichert] = useState("");

  useEffect(() => {
    if (abfrage.data && !entwurf) setEntwurf(abfrage.data.profile);
  }, [abfrage.data, entwurf]);

  const stand = abfrage.data
    ? abfrage.data.exists
      ? `${abfrage.data.profile.legal_name || "ohne Namen"} · ${abfrage.data.profile.bank?.iban || "keine IBAN"}`
      : "Noch nicht angelegt"
    : "wird geladen …";

  const feld = (wert: string, setzen: (v: string) => void, label: string, breit = false) => (
    <label className={`sd-feld${breit ? " weit" : ""}`} key={label}>
      <span>{label}</span>
      <input value={wert} onChange={(e) => setzen(e.target.value)} />
    </label>
  );

  return (
    <Karte
      titel="Firmenstamm"
      kicker="Absender auf jedem Beleg"
      stand={stand}
      kind={abfrage.data?.logo_data_uri
        ? <img className="sd-logo" src={abfrage.data.logo_data_uri} alt="Firmenlogo" />
        : null}
    >
      {(schliessen) => !entwurf ? <p className="sd-hinweis">wird geladen …</p> : (
        <>
          <p className="sd-hinweis">
            Diese Angaben stehen im Kopf und Fuß jedes Belegs — auch bei Belegen aus der
            Desktop-App, beide lesen dieselbe Datei.
          </p>
          <div className="sd-gitter">
            {feld(entwurf.legal_name, (v) => setEntwurf({ ...entwurf, legal_name: v }), "Firmenname", true)}
            <label className="sd-feld weit">
              <span>Anschrift (eine Zeile je Zeile)</span>
              <textarea
                rows={3}
                value={(entwurf.address?.lines || []).join("\n")}
                onChange={(e) => setEntwurf({
                  ...entwurf,
                  address: { ...entwurf.address, lines: e.target.value.split("\n") },
                })}
              />
            </label>
            {feld(entwurf.address?.postcode || "", (v) => setEntwurf({ ...entwurf, address: { ...entwurf.address, postcode: v } }), "PLZ")}
            {feld(entwurf.address?.city || "", (v) => setEntwurf({ ...entwurf, address: { ...entwurf.address, city: v } }), "Ort")}
            {feld(entwurf.phone || "", (v) => setEntwurf({ ...entwurf, phone: v }), "Telefon")}
            {feld(entwurf.email || "", (v) => setEntwurf({ ...entwurf, email: v }), "E-Mail")}
            {feld(entwurf.website || "", (v) => setEntwurf({ ...entwurf, website: v }), "Website")}
            {feld(entwurf.vat_id || "", (v) => setEntwurf({ ...entwurf, vat_id: v }), "USt-IdNr.")}
            {feld(entwurf.tax_number || "", (v) => setEntwurf({ ...entwurf, tax_number: v }), "Steuernummer")}
            {feld(entwurf.owners_line || "", (v) => setEntwurf({ ...entwurf, owners_line: v }), "Inhaberzeile", true)}
            {feld(entwurf.bank?.name || "", (v) => setEntwurf({ ...entwurf, bank: { ...entwurf.bank, name: v } }), "Bank")}
            {feld(entwurf.bank?.iban || "", (v) => setEntwurf({ ...entwurf, bank: { ...entwurf.bank, iban: v } }), "IBAN")}
            {feld(entwurf.bank?.bic || "", (v) => setEntwurf({ ...entwurf, bank: { ...entwurf.bank, bic: v } }), "BIC")}
            {feld(entwurf.footer_line || "", (v) => setEntwurf({ ...entwurf, footer_line: v }), "Fußzeile", true)}
          </div>
          <Meldung ok={gespeichert} fehler={speichern.error} />
          <div className="sd-aktionen">
            <button type="button" className="sd-btn" onClick={schliessen}>Abbrechen</button>
            <button
              type="button"
              className="sd-btn primary"
              disabled={speichern.isPending}
              onClick={() => speichern.mutate(entwurf, {
                onSuccess: () => setGespeichert("Firmenstamm gespeichert."),
              })}
            >
              {speichern.isPending ? "Speichert …" : "Speichern"}
            </button>
          </div>
        </>
      )}
    </Karte>
  );
}

/* ══ Textbausteine ═══════════════════════════════════════════════════════ */

const BAUSTEIN_GRUPPEN = [
  { schluessel: "intro_invoice", label: "Einleitung Rechnung", defaultsKey: "intro_invoice_label" },
  { schluessel: "notes_invoice", label: "Fußtext Rechnung", defaultsKey: "notes_invoice_label" },
  { schluessel: "intro_quote", label: "Einleitung Angebot", defaultsKey: "intro_quote_label" },
  { schluessel: "notes_quote", label: "Fußtext Angebot", defaultsKey: "notes_quote_label" },
] as const;

function TextbausteineKarte() {
  const abfrage = useTextbausteine();
  const speichern = useSaveTextbausteine();
  const [entwurf, setEntwurf] = useState<Textbausteine | null>(null);
  const [gespeichert, setGespeichert] = useState("");

  useEffect(() => { if (abfrage.data && !entwurf) setEntwurf(abfrage.data); }, [abfrage.data, entwurf]);

  const anzahl = abfrage.data
    ? BAUSTEIN_GRUPPEN.reduce((s, g) => s + (abfrage.data![g.schluessel]?.length || 0), 0)
    : 0;

  const setzeGruppe = (schluessel: string, bausteine: TextBaustein[]) => {
    if (entwurf) setEntwurf({ ...entwurf, [schluessel]: bausteine } as Textbausteine);
  };

  return (
    <Karte
      titel="Textbausteine"
      kicker="Einleitung und Fußtext"
      stand={abfrage.data ? `${anzahl} Bausteine in vier Gruppen` : "wird geladen …"}
    >
      {(schliessen) => !entwurf ? <p className="sd-hinweis">wird geladen …</p> : (
        <>
          <p className="sd-hinweis">
            Der als Standard markierte Baustein wird bei jedem neuen Beleg vorbelegt.
            Platzhalter wie <code>{"{{FAELLIGKEIT_DE}}"}</code> oder{" "}
            <code>{"{{ZAHLUNGSZIEL_TAGE}}"}</code> werden beim Einfügen ersetzt.
          </p>
          {BAUSTEIN_GRUPPEN.map((gruppe) => {
            const bausteine = entwurf[gruppe.schluessel] || [];
            const standard = entwurf.defaults?.[gruppe.defaultsKey] || "";
            return (
              <div className="sd-gruppe" key={gruppe.schluessel}>
                <div className="sd-gruppe-kopf">
                  <strong>{gruppe.label}</strong>
                  <label className="sd-standard">
                    <span>Standard</span>
                    <select
                      value={standard}
                      onChange={(e) => setEntwurf({
                        ...entwurf,
                        defaults: { ...entwurf.defaults, [gruppe.defaultsKey]: e.target.value },
                      })}
                    >
                      <option value="">— keiner —</option>
                      {bausteine.map((b) => <option key={b.label} value={b.label}>{b.label}</option>)}
                    </select>
                  </label>
                </div>
                {bausteine.map((baustein, i) => (
                  <div className="sd-baustein" key={`${gruppe.schluessel}-${i}`}>
                    <input
                      value={baustein.label}
                      placeholder="Kurzname"
                      onChange={(e) => setzeGruppe(gruppe.schluessel, bausteine.map((b, j) => j === i ? { ...b, label: e.target.value } : b))}
                    />
                    <textarea
                      rows={2}
                      value={baustein.text}
                      placeholder="Text"
                      onChange={(e) => setzeGruppe(gruppe.schluessel, bausteine.map((b, j) => j === i ? { ...b, text: e.target.value } : b))}
                    />
                    <button
                      type="button"
                      className="sd-weg"
                      aria-label="Baustein entfernen"
                      onClick={() => setzeGruppe(gruppe.schluessel, bausteine.filter((_, j) => j !== i))}
                    >
                      ×
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  className="sd-btn klein"
                  onClick={() => setzeGruppe(gruppe.schluessel, [...bausteine, { label: "", text: "" }])}
                >
                  + Baustein
                </button>
              </div>
            );
          })}
          <Meldung ok={gespeichert} fehler={speichern.error} />
          <div className="sd-aktionen">
            <button type="button" className="sd-btn" onClick={schliessen}>Abbrechen</button>
            <button
              type="button"
              className="sd-btn primary"
              disabled={speichern.isPending}
              onClick={() => speichern.mutate(entwurf, {
                onSuccess: () => setGespeichert("Textbausteine gespeichert."),
              })}
            >
              {speichern.isPending ? "Speichert …" : "Speichern"}
            </button>
          </div>
        </>
      )}
    </Karte>
  );
}

/* ══ Positionskatalog ════════════════════════════════════════════════════ */

function KatalogKarte() {
  const abfrage = useKatalog();
  const speichern = useSaveKatalog();
  const [entwurf, setEntwurf] = useState<KatalogPosition[] | null>(null);
  const [suche, setSuche] = useState("");
  const [gespeichert, setGespeichert] = useState("");

  useEffect(() => { if (abfrage.data && !entwurf) setEntwurf(abfrage.data.items); }, [abfrage.data, entwurf]);

  const sichtbar = (entwurf || []).map((p, i) => ({ p, i })).filter(({ p }) => {
    const q = suche.trim().toLowerCase();
    return !q || `${p.title} ${p.description}`.toLowerCase().includes(q);
  });

  const setze = (i: number, patch: Partial<KatalogPosition>) => {
    if (entwurf) setEntwurf(entwurf.map((p, j) => j === i ? { ...p, ...patch } : p));
  };

  return (
    <Karte
      titel="Positionskatalog"
      kicker="Leistungen und Material"
      stand={abfrage.data ? `${abfrage.data.items.length} Positionen` : "wird geladen …"}
    >
      {(schliessen) => !entwurf ? <p className="sd-hinweis">wird geladen …</p> : (
        <>
          <p className="sd-hinweis">
            Hier steht der <strong>vollständige</strong> Katalog — die Suche im Belegeditor
            zeigt nur die ersten 50 Treffer. Beim Speichern wird die Liste vollständig ersetzt.
          </p>
          <input
            className="sd-suche"
            value={suche}
            onChange={(e) => setSuche(e.target.value)}
            placeholder={`${entwurf.length} Positionen durchsuchen …`}
          />
          <div className="sd-katalog">
            {sichtbar.map(({ p, i }) => (
              <div className="sd-katalog-zeile" key={i}>
                <input value={p.title} placeholder="Titel" onChange={(e) => setze(i, { title: e.target.value })} />
                <input value={p.description} placeholder="Beschreibung" onChange={(e) => setze(i, { description: e.target.value })} />
                <input value={p.unit_label} placeholder="Einheit" onChange={(e) => setze(i, { unit_label: e.target.value })} />
                <input type="number" step="0.01" value={String(p.unit_price_net)} onChange={(e) => setze(i, { unit_price_net: Number(e.target.value) })} />
                <select value={String(p.vat_rate_percent)} onChange={(e) => setze(i, { vat_rate_percent: Number(e.target.value) })}>
                  <option value="19">19 %</option><option value="7">7 %</option><option value="0">0 %</option>
                </select>
                <button type="button" className="sd-weg" aria-label="Position entfernen" onClick={() => setEntwurf(entwurf.filter((_, j) => j !== i))}>×</button>
              </div>
            ))}
            {sichtbar.length === 0 && <p className="sd-hinweis">Kein Treffer für „{suche}".</p>}
          </div>
          <button
            type="button"
            className="sd-btn klein"
            onClick={() => setEntwurf([...entwurf, { title: "", description: "", unit_label: "Stück", unit_code: "C62", unit_price_net: 0, vat_rate_percent: 19 }])}
          >
            + Position
          </button>
          <Meldung ok={gespeichert} fehler={speichern.error} />
          <div className="sd-aktionen">
            <button type="button" className="sd-btn" onClick={schliessen}>Abbrechen</button>
            <button
              type="button"
              className="sd-btn primary"
              disabled={speichern.isPending}
              onClick={() => speichern.mutate(entwurf, {
                onSuccess: () => setGespeichert(`${entwurf.length} Positionen gespeichert.`),
              })}
            >
              {speichern.isPending ? "Speichert …" : "Speichern"}
            </button>
          </div>
        </>
      )}
    </Karte>
  );
}

/* ══ Einheiten ═══════════════════════════════════════════════════════════ */

function EinheitenKarte() {
  const abfrage = useEinheiten();
  const speichern = useSaveEinheiten();
  const [text, setText] = useState<string | null>(null);
  const [gespeichert, setGespeichert] = useState("");

  useEffect(() => { if (abfrage.data && text === null) setText(abfrage.data.items.join("\n")); }, [abfrage.data, text]);

  return (
    <Karte
      titel="Einheiten"
      kicker="Auswahl in der Positionszeile"
      stand={abfrage.data ? abfrage.data.items.slice(0, 6).join(" · ") + (abfrage.data.items.length > 6 ? " …" : "") : "wird geladen …"}
    >
      {(schliessen) => text === null ? <p className="sd-hinweis">wird geladen …</p> : (
        <>
          <p className="sd-hinweis">Eine Einheit je Zeile. Doppelte werden beim Speichern zusammengefasst.</p>
          <textarea className="sd-liste" rows={10} value={text} onChange={(e) => setText(e.target.value)} />
          <Meldung ok={gespeichert} fehler={speichern.error} />
          <div className="sd-aktionen">
            <button type="button" className="sd-btn" onClick={schliessen}>Abbrechen</button>
            <button
              type="button"
              className="sd-btn primary"
              disabled={speichern.isPending}
              onClick={() => speichern.mutate(
                text.split("\n").map((z) => z.trim()).filter(Boolean),
                { onSuccess: (d) => setGespeichert(`${d.items.length} Einheiten gespeichert.`) },
              )}
            >
              {speichern.isPending ? "Speichert …" : "Speichern"}
            </button>
          </div>
        </>
      )}
    </Karte>
  );
}

/* ══ Nummernkreis ════════════════════════════════════════════════════════ */

const KREIS_LABEL: Record<string, string> = { invoice: "Rechnungen", quote: "Angebote" };

function NummernkreisKarte() {
  const abfrage = useNummernkreise();
  const speichern = useSaveNummernkreis();
  const kreise = abfrage.data?.items || [];

  return (
    <Karte
      titel="Nummernkreis"
      kicker="Verbindlich für Kontor und Desktop"
      stand={kreise.length ? kreise.map((k) => `${KREIS_LABEL[k.kind] || k.kind}: ${k.number}`).join(" · ") : "wird geladen …"}
    >
      {() => (
        <>
          <p className="sd-hinweis">
            Abschlags- und Schlussrechnungen teilen sich den Rechnungskreis. Beide Apps
            ziehen aus derselben Zeile — eine Änderung wirkt sofort auch im Desktop.
          </p>
          {kreise.map((kreis) => <KreisZeile key={kreis.kind} kreis={kreis} speichern={speichern} />)}
        </>
      )}
    </Karte>
  );
}

function KreisZeile({ kreis, speichern }: {
  kreis: Nummernkreis;
  speichern: ReturnType<typeof useSaveNummernkreis>;
}) {
  const [entwurf, setEntwurf] = useState(kreis);
  const [gespeichert, setGespeichert] = useState("");
  const [warnung, setWarnung] = useState("");

  const zurueck = entwurf.next_value < kreis.next_value;
  const stellenWechsel = entwurf.width !== kreis.width;
  const vorschau = `${entwurf.prefix}${String(entwurf.next_value).padStart(entwurf.width, "0")}`;

  const sichern = (force: boolean) => speichern.mutate(
    { kind: kreis.kind, patch: entwurf, force },
    {
      onSuccess: () => { setGespeichert(`${KREIS_LABEL[kreis.kind]} gespeichert.`); setWarnung(""); },
      onError: (fehler) => {
        // Der Server lehnt Rückwärtsgänge ab. Seine Begründung ist die
        // bessere Warnung als eine selbst gebaute — also durchreichen.
        if (zurueck || stellenWechsel) setWarnung(errorText(fehler));
      },
    },
  );

  return (
    <div className="sd-kreis">
      <div className="sd-kreis-kopf">
        <strong>{KREIS_LABEL[kreis.kind] || kreis.kind}</strong>
        <span>bisher höchste: {kreis.hoechste_vergeben || "noch keine"}</span>
      </div>
      <div className="sd-gitter">
        <label className="sd-feld"><span>Präfix</span><input value={entwurf.prefix} onChange={(e) => setEntwurf({ ...entwurf, prefix: e.target.value })} /></label>
        <label className="sd-feld"><span>Stellen</span><input type="number" min="1" max="12" value={entwurf.width} onChange={(e) => setEntwurf({ ...entwurf, width: Number(e.target.value) || 1 })} /></label>
        <label className="sd-feld"><span>Nächste Nummer</span><input type="number" min="1" value={entwurf.next_value} onChange={(e) => setEntwurf({ ...entwurf, next_value: Number(e.target.value) || 1 })} /></label>
        <label className="sd-feld"><span>Zahlungsziel (Tage)</span><input type="number" min="0" max="365" value={entwurf.default_payment_term_days} onChange={(e) => setEntwurf({ ...entwurf, default_payment_term_days: Number(e.target.value) || 0 })} /></label>
      </div>
      <p className="sd-vorschau">Der nächste Beleg bekommt <strong>{vorschau}</strong>.</p>
      {warnung && (
        <div className="sd-warnung" role="alert">
          <p>{warnung}</p>
          <button type="button" className="sd-btn gefahr" onClick={() => sichern(true)}>
            Verstanden, trotzdem speichern
          </button>
        </div>
      )}
      <Meldung ok={gespeichert} fehler={warnung ? undefined : speichern.error} />
      <div className="sd-aktionen">
        <button type="button" className="sd-btn" onClick={() => { setEntwurf(kreis); setWarnung(""); }}>Zurücksetzen</button>
        <button type="button" className="sd-btn primary" disabled={speichern.isPending} onClick={() => sichern(false)}>
          {speichern.isPending ? "Speichert …" : "Speichern"}
        </button>
      </div>
    </div>
  );
}

/* ══ Beleg-Vorlagen ══════════════════════════════════════════════════════ */

function VorlagenKarte() {
  const abfrage = useVorlagen();
  const loeschen = useDeleteVorlage();
  const vorlagen = abfrage.data?.items || [];

  return (
    <Karte
      titel="Beleg-Vorlagen"
      kicker="Wiederkehrende Leistungen"
      stand={vorlagen.length ? `${vorlagen.length} Vorlagen` : "Noch keine Vorlage"}
    >
      {() => (
        <>
          <p className="sd-hinweis">
            Eine Vorlage enthält Positionen und Texte — Kunde, Nummer und Daten bleiben
            bewusst draußen. Angelegt werden sie im Belegeditor über „Als Vorlage sichern".
          </p>
          {vorlagen.length === 0
            ? <p className="sd-hinweis">Noch nichts gesichert.</p>
            : vorlagen.map((v) => (
              <div className="sd-vorlage" key={v.id}>
                <div>
                  <strong>{v.name}</strong>
                  <small>{v.positionen} Positionen · {v.summe_netto.toLocaleString("de-DE", { style: "currency", currency: "EUR" })} netto{v.beschreibung ? ` · ${v.beschreibung}` : ""}</small>
                </div>
                <button
                  type="button"
                  className="sd-weg"
                  aria-label={`Vorlage ${v.name} löschen`}
                  disabled={loeschen.isPending}
                  onClick={() => { if (window.confirm(`Vorlage „${v.name}" löschen?`)) loeschen.mutate(v.id); }}
                >
                  ×
                </button>
              </div>
            ))}
          <Meldung fehler={loeschen.error} />
        </>
      )}
    </Karte>
  );
}

export default function StammdatenSektionen() {
  return (
    <>
      <FirmenstammKarte />
      <TextbausteineKarte />
      <KatalogKarte />
      <EinheitenKarte />
      <NummernkreisKarte />
      <VorlagenKarte />
    </>
  );
}
